import { collection, documentId, orderBy, query, where } from 'firebase/firestore';
import type { Query, QueryConstraint } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import { SHIFT_TYPES_OPTIONS } from '@src/packages/shifts/constants';
import type { ShiftFiltersState } from '@src/packages/shifts/types';
import { SHIFT_FILTER_DEFAULTS } from '@src/packages/shifts/shiftsListFilterConfig';
import { expandDaysOfWeekForFirestoreQuery } from '@src/packages/utils/dates/formatDaysOfWeek';
import {
  chunkIds,
  FIRESTORE_IN_LIMIT,
} from '@src/packages/users/screens/UsersList/utils/buildUserQueries';

export const hasShiftStatusFilter = (
  status: ShiftFiltersState['status'],
): boolean => Boolean(status && status !== SHIFT_FILTER_DEFAULTS.STATUS);

export const getShiftListFirestoreConstraints = (
  filters: Pick<ShiftFiltersState, 'shiftType' | 'selectedDays' | 'status'>,
  includeExceptional = true,
): QueryConstraint[] => {
  const constraints: QueryConstraint[] = [];

  if (hasShiftStatusFilter(filters.status)) {
    constraints.push(where('status', '==', filters.status));
  }

  if (filters.shiftType && filters.shiftType !== SHIFT_FILTER_DEFAULTS.SHIFT_TYPE) {
    if (filters.shiftType === SHIFT_TYPES_OPTIONS.EXCEPTIONAL && includeExceptional) {
      constraints.push(where('hasPolicies', '==', true));
    } else if (filters.shiftType !== SHIFT_TYPES_OPTIONS.EXCEPTIONAL) {
      constraints.push(where('type', '==', filters.shiftType));
    }
  }

  if (filters.selectedDays.length > 0) {
    const days = expandDaysOfWeekForFirestoreQuery(filters.selectedDays);
    if (days.length > 0) {
      constraints.push(where('daysOfWeek', 'array-contains-any', days));
    }
  }

  return constraints;
};

/**
 * Builds Firestore realtime queries for the shifts list.
 * `ids === null` → collection query (no Elastic). Empty `ids` → no matches.
 */
export const buildShiftQueries = (
  orgId: string,
  ids: string[] | null,
  filters: Pick<ShiftFiltersState, 'shiftType' | 'selectedDays' | 'status'>,
): Query[] => {
  if (!orgId) {
    return [];
  }

  const baseCollection = collection(
    db,
    FSCollections.ORGANIZATIONS,
    orgId,
    FSCollections.WORK_SHIFTS,
  );
  const firestoreConstraints = getShiftListFirestoreConstraints(filters, ids == null);
  const shouldOrderByCreatedAt = !hasShiftStatusFilter(filters.status);

  if (ids == null) {
    return [
      query(
        baseCollection,
        ...firestoreConstraints,
        ...(shouldOrderByCreatedAt ? [orderBy('createdAt', 'desc')] : []),
      ),
    ];
  }

  if (ids.length === 0) {
    return [];
  }

  return chunkIds(ids, FIRESTORE_IN_LIMIT).map(idChunk =>
    query(baseCollection, where(documentId(), 'in', idChunk), ...firestoreConstraints),
  );
};

export { FIRESTORE_IN_LIMIT };
